import {
  Autocomplete,
  Fab,
  InputAdornment,
  Paper,
  TextField,
  useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import GridViewIcon from "@mui/icons-material/GridView";
import styles from "./styles.module.css";
import React, { useEffect, useState } from "react";
import TableView from "./TableView";
import CardView from "./CardView";
import AddIcon from "@mui/icons-material/Add";
import { keyframes } from "@emotion/react";
import { debounce } from "lodash";
import Pagination from "../Pagination";
import BtnGenericHeader from "./BtnGenericHeader";
import { buscarComboPorCodigo } from "../../services/auth.service";
import Loading from "../Loading";

//DOCUMENTADO NO FINAL DO ARQUIVO

const TableCard = ({
  iconHeader,
  titleHeader,
  /** Nó renderizado na MESMA linha do título (justify-content:space-between empurra pra
   * direita) — diferente de hoverBtnHeader, que fica na linha dos ícones de visualização e
   * SOME pra uma linha própria quando customFiltersFullWidth força o painel de filtros a
   * 100% de largura (flex-wrap quebra linha). Usar quando o botão precisa ficar ao lado do
   * título mesmo havendo um painel de filtros largo abaixo (TXM-594 Tabela NCM). */
  actionHeader,
  /** true força o cabeçalho "Ações" a aparecer mesmo com data=[] — por padrão (undefined) o
   * comportamento é o de sempre: hasBotoes vem de data.some(...), que é sempre false num
   * array vazio, então uma tabela paginada no servidor que carrega sem filtro nenhum some a
   * coluna assim que a página fica vazia. Opt-in por tela, não muda ninguém que já existe. */
  hasActions,
  headerSx,
  optionsFilter,
  textHeaderCard,
  data,
  setData,
  columns,
  handleDebounce,
  temInsert = false,
  insertOpenModal = null,
  insertFabVariant = "default",
  insertFabColor = "#4b6fc0",
  filterSelected,
  hoverBtnHeader,
  handleGrupoEmpresa,
  hasEmpresa,
  loading,
  uniqueEmpresa,
  selectedAllEpresas,
  columnWidths,
  customFilters,
  customFiltersFullWidth = false,
  tableClassName,
  tableMinWidth,
  stickyActions = false,
  onStatusHeaderClick,
  statusHeaderRef,
  onCategoriaHeaderClick,
  categoriaHeaderRef,
  contentAboveTable,
  contentAboveTableGrid = false,
  contentAboveTableGridCols,
  contentAboveTableBg,
  hideViewToggle = false,
  paginationKey: paginationKeyProp,
  /** TXM-219: { total, page, rowsPerPage, onPageChange, onRowsPerPageChange } */
  serverPagination = null,
}) => {
  const minDesktop = useMediaQuery("(max-width: 900px)");
  const maxCelular = useMediaQuery("(max-width: 470px)");
  const [visualizacao, setVisualizacao] = useState(
    minDesktop ? "grid" : "table"
  );
  const effectiveVisualizacao = hideViewToggle ? "table" : visualizacao;
  const [pesquisa, setPesquisa] = useState("");
  const [selectedOptions, setSelectedOptions] = useState(
    filterSelected ? [{ value: filterSelected }] : []
  );
  /** Lista já filtrada por Autocomplete (base para paginação client-side). */
  const [baseForPagination, setBaseForPagination] = useState(() =>
    Array.isArray(data) ? data : []
  );
  const [filteredData, setFilteredData] = useState(() =>
    Array.isArray(data) ? data.slice(0, 10) : []
  );
  const [optionsGrupoEmpresas, setOptionsGrupoEmpresas] = useState({
    GE: [],
    CNPJ: [],
  });
  const [grupoEmpresas, setGrupoEmpresas] = useState({
    GE: null,
    CNPJ: uniqueEmpresa ? null : [],
  });

  const styleAutoComplete = {
    width: "100%",
    minWidth: maxCelular ? "" : "400px",
    "& .MuiOutlinedInput-root": {
      borderRadius: "50px",
      "& fieldset": {
        borderColor: "#18316e",
      },
      "&:hover fieldset": {
        borderColor: "#18316e",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#18316e",
      },
    },
  };

  const styleHeader = {
    display: "flex",
    gap: "1rem",
    flexWrap: handleGrupoEmpresa ? "wrap" : minDesktop ? "wrap" : "nowrap",
    maxWidth: handleGrupoEmpresa ? "400px" : "",
  };

  useEffect(() => {
    setVisualizacao(minDesktop ? "grid" : "table");
  }, [minDesktop]);

  const extractText = (value) => {
    if (typeof value === "string") {
      return value;
    } else if (React.isValidElement(value)) {
      let extractedString = "";
      React.Children.forEach(value.props.children, (child) => {
        if (typeof child === "string") {
          extractedString += child;
        } else if (React.isValidElement(child)) {
          React.Children.forEach(child.props.children, (nestedChild) => {
            if (typeof nestedChild === "string") {
              extractedString += nestedChild;
            }
          });
        }
      });
      return extractedString.trim();
    }
    return "";
  };

  const recomputeBaseForPagination = () => {
    if (!Array.isArray(data)) {
      setBaseForPagination([]);
      return;
    }
    if (selectedOptions.length === 0) {
      setBaseForPagination([...data]);
      return;
    }
    const filtered = data.filter((item) => {
      return selectedOptions.every((option) => {
        return Object.values(item).some((value) => {
          if (value == null) return false;
          if (Array.isArray(value)) {
            return value.some((button) => button.title === option.value);
          }
          const textValue = extractText(value);
          return textValue.includes(option.value);
        });
      });
    });
    setBaseForPagination(filtered.length ? filtered : []);
  };

  useEffect(() => {
    recomputeBaseForPagination();
  }, [selectedOptions, data]);

  const serverEnabled =
    serverPagination != null &&
    serverPagination.total != null &&
    typeof serverPagination.onPageChange === "function" &&
    typeof serverPagination.onRowsPerPageChange === "function";

  useEffect(() => {
    if (!serverEnabled) return;
    const rows = Array.isArray(data) ? [...data] : [];
    setFilteredData(rows);
    setBaseForPagination(rows);
  }, [data, serverEnabled, serverPagination?.total, serverPagination?.page, serverPagination?.rowsPerPage]);

  const handleCombos = async (codCombo) => {
    const response = await buscarComboPorCodigo(codCombo);
    const combosFormat = response.detalhes.itensCombo
      .filter((item) => item)
      .map((item) => {
        const split = item.split("/|/");
        return {
          id: split[0],
          detalhes: split[1],
        };
      });

    const uniqueCombos = combosFormat.filter(
      (combo, index, self) =>
        index === self.findIndex((t) => t.detalhes === combo.detalhes)
    );

    return uniqueCombos;
  };

  useEffect(() => {
    handleGrupoEmpresa && handleGrupoEmpresa(grupoEmpresas);
  }, [grupoEmpresas]);

  useEffect(() => {
    const fetchGrupos = async () => {
      const grupos = await handleCombos("GRUPOECONOMICO");
      setOptionsGrupoEmpresas({ ...optionsGrupoEmpresas, GE: grupos });
    };

    handleGrupoEmpresa && fetchGrupos();
  }, []);

  useEffect(() => {
    const fetchEmpresas = async () => {
      const empresas = await handleCombos(
        `EMPRESA_POR_GRUPO_TABLECARD_${grupoEmpresas.GE?.id}`
      );
      const empresasMask = empresas.map((item) => {
        return {
          ...item,
          detalhes: item.detalhes.replace(
            /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
            "$1.$2.$3/$4-$5"
          ),
        };
      });
      setOptionsGrupoEmpresas({ ...optionsGrupoEmpresas, CNPJ: empresasMask });
      selectedAllEpresas &&
        setGrupoEmpresas({ ...grupoEmpresas, CNPJ: empresas });
    };
    if (hasEmpresa && grupoEmpresas.GE) fetchEmpresas();
  }, [grupoEmpresas.GE]);

  const debouncedHandleChangeSearchText = handleDebounce
    ? debounce(handleDebounce, 500)
    : null;

  const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
`;

  const handleFilter = (_, value) => {
    const mapValue = value.map((item) => {
      if (item.value.includes(":")) {
        const splitValue = item.value.split(":");
        return { value: splitValue[1].trim() };
      } else {
        return { value: item.value };
      }
    });

    setSelectedOptions(mapValue);
  };

  const isOptionEqualToValue = (option, value) => {
    const [_, optionValue] = option.value.split(":").map((s) => s.trim());
    return optionValue === value.value;
  };

  const handleChangeVisualizacao = (name) => {
    setVisualizacao(name);
  };

  const handleChangeGECNPJ = (name, value) => {
    switch (name) {
      case "GE":
        if (value) {
          setGrupoEmpresas({ ...grupoEmpresas, GE: value });
        } else {
          setGrupoEmpresas({ GE: null, CNPJ: uniqueEmpresa ? null : [] });
          setOptionsGrupoEmpresas({ ...optionsGrupoEmpresas, CNPJ: [] });
        }
        break;
      case "CNPJ":
        if (value) {
          setGrupoEmpresas({ ...grupoEmpresas, CNPJ: value });
        } else {
          setGrupoEmpresas({
            ...grupoEmpresas,
            CNPJ: uniqueEmpresa ? null : [],
          });
        }
        break;

      default:
        break;
    }
  };

  const handleOrder = (key, order, setOrder) => {
    const lista = [...data];
    let listaOrdenada = [];

    const extraiTexto = (texto) => {
      if (typeof texto === "number") {
        return texto;
      }

      if (typeof texto === "string") {
        const verificacao = () => {
          switch (true) {
            case texto.includes("R$"):
              return texto
                .replace("R$", "")
                .replaceAll(".", "")
                .replace(",", ".");
            case texto.includes("%"):
              return texto
                .replace("%", "")
                .replaceAll(".", "")
                .replace(",", ".");

            default:
              return texto;
          }
        };
        return verificacao();
      }

      if (!texto || !texto.props || !texto.props.children) {
        return "";
      }

      const children = texto.props.children;
      if (Array.isArray(children)) {
        return children
          .map((child) =>
            typeof child === "string" || typeof child === "number"
              ? child.toString()
              : ""
          )
          .join("")
          .trim();
      } else if (typeof children === "string" || typeof children === "number") {
        return children.toString().trim();
      }

      return "";
    };

    const parseDate = (dateString) => {
      const [day, month, year] = dateString.split("/");
      return new Date(`${year}-${month}-${day}`);
    };

    const isDate = (value) => {
      if (typeof value !== "string") return false; // Certifique-se de que é uma string
      const parts = value.split("/");
      if (parts.length !== 3) return false;
      const [day, month, year] = parts.map(Number);
      return (
        !isNaN(day) &&
        day > 0 &&
        day <= 31 &&
        !isNaN(month) &&
        month > 0 &&
        month <= 12 &&
        !isNaN(year) &&
        year > 0
      );
    };

    const comparaItens = (a, b) => {
      const itemA = extraiTexto(a[key]);
      const itemB = extraiTexto(b[key]);

      // Comparar datas
      if (isDate(itemA) && isDate(itemB)) {
        return parseDate(itemA) - parseDate(itemB);
      }

      // Comparar números
      const numA = parseFloat(itemA);
      const numB = parseFloat(itemB);
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }

      // Comparar strings
      return itemA.localeCompare(itemB);
    };

    if (order.lastKey === key) {
      switch (order.order) {
        case "asc":
          listaOrdenada = lista.sort(comparaItens);
          setOrder({ lastKey: key, order: "desc" });
          break;
        case "desc":
          listaOrdenada = lista.sort((a, b) => comparaItens(b, a));
          setOrder({ lastKey: key, order: "asc" });
          break;
        default:
          break;
      }
    } else {
      listaOrdenada = lista.sort(comparaItens);
      setOrder({ lastKey: key, order: "desc" });
    }
    setData(listaOrdenada);
    // setFilteredData(listaOrdenada);
  };

  return (
    <Paper className={styles.container}>
      {/* Card interno único: header + stats + tabela */}
      <div className={styles.innerCard}>

        {/* Header */}
        <div className={styles.header} style={headerSx}>
          <div className={styles.title}>
            {iconHeader}
            {titleHeader}
          </div>
          {actionHeader && (
            <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
              {actionHeader}
            </div>
          )}
          <div
            className={`${styles.filtrosContainer} ${
              customFiltersFullWidth ? styles.filtrosContainerFullWidth : ""
            }`}
            style={
              customFiltersFullWidth
                ? {
                    flex: "1 1 100%",
                    width: "100%",
                  }
                : undefined
            }
          >
            <div
              className={styles.headerCampos}
              style={customFiltersFullWidth ? { width: "100%" } : undefined}
            >
              <div
                className={styles.inputs_header}
                style={customFiltersFullWidth ? { width: "100%" } : undefined}
              >
                {handleGrupoEmpresa && (
                  <div style={{ width: "100%" }}>
                    <Autocomplete
                      value={grupoEmpresas.GE}
                      sx={styleAutoComplete}
                      id="tags-outlined"
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      options={optionsGrupoEmpresas.GE || []}
                      getOptionLabel={(option) => option.detalhes}
                      onChange={(_, value) => {
                        handleChangeGECNPJ("GE", value);
                        setGrupoEmpresas((prev) => ({ ...prev, CNPJ: uniqueEmpresa ? null : [] }));
                      }}
                      renderInput={(params) => (
                        <TextField {...params} size="small" variant="outlined" label="Grupo Economico" />
                      )}
                    />
                    {hasEmpresa && (
                      <div style={{ marginTop: "1rem" }}>
                        <Autocomplete
                          value={grupoEmpresas.CNPJ}
                          sx={styleAutoComplete}
                          disableCloseOnSelect={uniqueEmpresa ? false : true}
                          multiple={uniqueEmpresa ? false : true}
                          limitTags={1}
                          id="tags-outlined"
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          noOptionsText="Nenhuma empresa encontrada."
                          options={optionsGrupoEmpresas.CNPJ}
                          getOptionLabel={(option) => option.detalhes}
                          onChange={(_, value) => handleChangeGECNPJ("CNPJ", value)}
                          renderInput={(params) => (
                            <TextField {...params} size="small" variant="outlined" label="Empresas" />
                          )}
                        />
                      </div>
                    )}
                  </div>
                )}
                <div style={customFiltersFullWidth ? { ...styleHeader, width: "100%" } : styleHeader}>
                  {customFilters ? (
                    customFilters
                  ) : (
                    <>
                      {optionsFilter && (
                        <Autocomplete
                          value={selectedOptions}
                          sx={styleAutoComplete}
                          multiple
                          id="tags-outlined"
                          isOptionEqualToValue={isOptionEqualToValue}
                          options={optionsFilter}
                          getOptionLabel={(option) => option.value}
                          onChange={handleFilter}
                          renderInput={(params) => (
                            <TextField {...params} size="small" variant="outlined" label="Filtrar por..." />
                          )}
                        />
                      )}
                      {handleDebounce && (
                        <TextField
                          sx={styleAutoComplete}
                          label="Buscar por campos..."
                          id="outlined-start-adornment"
                          onChange={(e) => {
                            debouncedHandleChangeSearchText?.(e.target.value);
                            setPesquisa(e.target.value);
                          }}
                          value={pesquisa}
                          size="small"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon sx={{ color: "#18316e" }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          {(!hideViewToggle || (hoverBtnHeader && hoverBtnHeader.length > 0)) && (
            <div className={styles.tipoVisualizacaoContainer}>
              <div className={styles.headerButtons}>
                {hoverBtnHeader && hoverBtnHeader.length > 0
                  ? hoverBtnHeader.map((button, index) => (
                      <BtnGenericHeader
                        key={button.label || index}
                        icon={button.icon}
                        label={button.label}
                        onClick={button.onClick}
                        disabled={button.disabled}
                        alwaysOpen={button.alwaysOpen}
                        variant={button.variant}
                      />
                    ))
                  : ""}
                {!hideViewToggle && (
                  <div>
                    <FormatListBulletedIcon
                      className={visualizacao === "table" ? styles.iconVisualizacoesActive : styles.iconVisualizacoes}
                      onClick={() => handleChangeVisualizacao("table")}
                    />
                    <GridViewIcon
                      className={visualizacao === "grid" ? styles.iconVisualizacoesActive : styles.iconVisualizacoes}
                      onClick={() => handleChangeVisualizacao("grid")}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Cards de estatísticas */}
        {contentAboveTable && (
          <div
            className={contentAboveTableGrid ? styles.contentAboveTableGrid4 : styles.contentAboveTable}
            style={{
              ...(contentAboveTableBg ? { gap: "12px" } : undefined),
              ...(contentAboveTableGrid && contentAboveTableGridCols
                ? { gridTemplateColumns: `repeat(${contentAboveTableGridCols}, 1fr)` }
                : undefined),
            }}
          >
            {contentAboveTable}
          </div>
        )}

        {/* Linha separadora de borda a borda */}
        <div className={styles.tableDivider} />

        {/* Tabela */}
        {loading ? (
          <Loading loading={loading} />
        ) : (
          <>
            <div className={styles.visualizacaoContainer}>
              {effectiveVisualizacao === "table" ? (
                <TableView
                  data={filteredData}
                  columns={columns}
                  handleOrder={handleOrder}
                  columnWidths={columnWidths}
                  tableClassName={tableClassName}
                  tableMinWidth={tableMinWidth}
                  stickyActions={stickyActions}
                  onStatusHeaderClick={onStatusHeaderClick}
                  statusHeaderRef={statusHeaderRef}
                  onCategoriaHeaderClick={onCategoriaHeaderClick}
                  categoriaHeaderRef={categoriaHeaderRef}
                  hasActions={hasActions}
                />
              ) : (
                <CardView
                  data={filteredData}
                  columns={columns}
                  textHeaderCard={textHeaderCard}
                />
              )}
            </div>
            <Pagination
              paginationKey={
                paginationKeyProp ||
                `tablecard-${String(titleHeader || "lista")}-${selectedOptions.map((o) => o.value).join("|")}`
              }
              data={baseForPagination}
              filteredData={filteredData}
              setFilteredData={setFilteredData}
              serverSideTotal={serverEnabled ? serverPagination.total : undefined}
              serverSidePage={serverEnabled ? serverPagination.page : undefined}
              serverSideRowsPerPage={serverEnabled ? serverPagination.rowsPerPage : undefined}
              onServerPageChange={serverEnabled ? serverPagination.onPageChange : undefined}
              onServerRowsPerPageChange={serverEnabled ? serverPagination.onRowsPerPageChange : undefined}
            />
          </>
        )}

      </div>{/* fim innerCard */}

      {temInsert && (
        <Fab
          sx={{
            background: insertFabColor,
            bottom: 40,
            right: 20,
            position: "fixed",
            zIndex: 1000,
            animation: `${pulse} 1.5s infinite ease-in-out`,
            "&:hover":
              insertFabVariant === "vinculos"
                ? { backgroundColor: insertFabColor }
                : { backgroundColor: "#fff", "& .MuiSvgIcon-root": { color: insertFabColor } },
          }}
          aria-label="add"
          onClick={() => insertOpenModal()}
        >
          <AddIcon sx={{ color: "#fff" }} />
        </Fab>
      )}
    </Paper>
  );
};

export default TableCard;

//Exemplos de como consumir o componente:

//COMO IMPORTAR
{
  /* <TableCard
        iconHeader={<ContactPageIcon fontSize='1rem'/>} //Ícone do titulo da página
        titleHeader="Análises" //Titulo da página
        optionsFilter={optionsFilter} //OPCIONAL - Opções pré definidas de filtro
        data={data} //dados da tabela
        setData={setData} // função que alterada dados da tabela
        columns={columns} //colunas - OBS: Devem estar na mesma ordem dos dados
        textHeaderCard='nome' //campo que será header do card - OBS: deve ser o mesmo nome da tag enviada em data
        handleDebounce={handlePesquisaPorTexto} //função de pesquisa
        temInsert={true} //OPCIONAL - Renderiza o botão 'Fab' pulsante
        insertOpenModal={handleInsert} //OPCIONAL - Função de Click do botão de Insert
        hoverBtnHeader={botaoRelatorio} // OPCIONAL - Lista de botões hover
        handleGrupoEmpresa={handleGrupoEmpresa} // OPCIONAL - função para renderizar grupoeconomico e receber os ids selecionados
        hasEmpresa // OPCIONAL - não há necessidade de colocar nenhum parametro. Serve para renderizar o combo de empresas
        loading={loading} //OPCIONAL - boolean para loading dinamico
    />   
    */
}

//AS OPÇÕES DE FILTROS DEVEM SER ENVIADAS EXATAMENTE COMO ABAIXO
//Cada value PRECISA estar no formato 'campoDaColuna: Filtro'
// const optionsFilter = [
//     {value: 'tipo: Cliente'},
//     {value: 'tipo: Parceiro'},
//     {value: 'tipo: Associado'},
//     {value: 'status: Ativo'},
//     {value: 'status: Inativo'},
//     {value: 'status: Em Análise'},
//   ]

//Para os botoes funcionarem, deve ser enviado EM CADA ITEM DA LISTA, na tag 'botoes'
//uma lista de objetos com as tags(title, icon, onClick)

// const data = [
//   {
//     tipo: 'Cliente',
//     nome: 'Nome 1',
//     cpf: '123.456.789-00',
//     cnpj: '12.345.678/0001-99',
//     whatsapp: '1111-1111',
//     status: 'Ativo',
//     botoes: [
//       { icon: <VisibilityIcon />, title: 'VISUALIZAR', onClick:() => handleClick() }
//     ]
//   },
//   {
//     tipo: 'Associado',
//     nome: 'Nome 2',
//     cpf: '987.654.321-00',
//     cnpj: '98.765.432/0001-99',
//     whatsapp: '2222-2222',
//     status: 'Inativo',
//     botoes: [
//       { icon: <VisibilityIcon />, title: 'VISUALIZAR', onClick: () => handleClick() },
//       { icon: <PeopleIcon />, title: 'PARCEIROS', onClick: () => handleClick() },
//       { icon: <GroupsIcon />, title: 'CLIENTES', onClick: () => handleClick() },
//     ]
//   },
//   {
//     tipo: 'Parceiro',
//     nome: 'Nome 3',
//     cpf: '123.456.789-00',
//     cnpj: '12.345.678/0001-99',
//     whatsapp: '1111-1111',
//     status: 'Em Análise',
//     botoes: [
//       { icon: <AttachMoneyIcon />, title: 'NEGOCIAÇÃO', onClick: () => handleClick() },
//       { icon: <VisibilityIcon />, title: 'VISUALIZAR', onClick: () => handleClick() }
//     ]
//   },
//   {
//     tipo: 'Parceiro',
//     nome: 'Nome 3',
//     cpf: '123.456.789-00',
//     cnpj: '12.345.678/0001-99',
//     whatsapp: '1111-1111',
//     status: 'Ativo',
//     botoes: [
//       { icon: <AttachMoneyIcon />, title: 'NEGOCIAÇÃO', onClick: () => handleClick() },
//       { icon: <VisibilityIcon />, title: 'VISUALIZAR', onClick: () => handleClick() }
//     ]
//   },
// ];

// const columns = [
//   'Tipo', 'Nome', 'CPF', 'CNPJ', 'WhatsApp', 'Status'
// ]

// const botaoRelatorio = [
//   {
//     icon: <DescriptionIcon />,
//     label: "RELATÓRIO",
//     onClick: handleRelatorioModal,
//   },
// ];

// const handleGrupoEmpresa = (ids) => {
// ids recebe {GE: {}, CNPJ: []}
// }
